import { inject } from 'vue'
import {
  estimatedTimeNamesKey,
  type EstimatedTimeNamesService
} from '@gat-ship/types/injection-keys'

/**
 * Inject the estimated-time-names service provided by a parent
 * `EstimatedTimeNamesProvider`.
 *
 * Throws when no provider is mounted — fail fast rather than papering
 * over a missing provider with an empty list.
 *
 * Consumers in other features can equivalently call
 * `inject(estimatedTimeNamesKey)` directly with the key from
 * `@gat-ship/types/injection-keys` and skip the dependency on this
 * package; this composable just bundles the null-check.
 */
export function useEstimatedTimeNames(): EstimatedTimeNamesService {
  const service = inject(estimatedTimeNamesKey)
  if (!service) {
    throw new Error(
      'useEstimatedTimeNames() called without an <EstimatedTimeNamesProvider> ancestor.'
    )
  }
  return service
}

/**
 * Optional variant — returns `undefined` instead of throwing when no
 * provider is mounted. Useful for components that should degrade
 * gracefully (e.g. show a static placeholder).
 */
export function useEstimatedTimeNamesOptional():
  | EstimatedTimeNamesService
  | undefined {
  return inject(estimatedTimeNamesKey, undefined)
}
